import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
  LayoutDashboard,
  Wallet,
  Boxes,
  Users,
  BarChart3,
  Settings,
  LogOut,
  MoreHorizontal,
  LineChart,
  Upload,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = (t, user) => {
  const role = user?.role || "";
  const isSuper = !!user?.is_super_admin;
  const isStaff = ["staff", "waiter", "kitchen"].includes(role);
  const isAccountant = role === "accountant";
  const managerial = ["owner", "restaurant_owner", "manager", "restaurant_manager",
                       "organization_owner", "organization_admin", "brand_manager", "regional_manager"];
  const canManagePersonnel = isSuper || managerial.includes(role);
  const canSeeFinance = canManagePersonnel || isAccountant;
  const canSeeReports = canSeeFinance;
  const canSeeForecast = canSeeFinance;

  // Hotfix — data-entry-only tier: hide EVERY financial / analytical link.
  // The Dashboard route renders the simplified StaffDashboard for these
  // roles and no other page is reachable from the sidebar.
  if (isStaff && !isSuper) {
    const base = [
      { to: "/", label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
    ];
    if (role === "kitchen") {
      base.push({ to: "/inventory", label: t("inventory"), icon: Boxes, testid: "nav-inventory" });
    }
    return base;
  }

  const base = [
    { to: "/", label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
  ];
  if (canSeeFinance) base.push({ to: "/finance", label: t("finance"), icon: Wallet, testid: "nav-finance" });
  if (canSeeForecast) base.push({ to: "/forecast", label: t("forecast"), icon: LineChart, testid: "nav-forecast" });
  // Imports permitted for accountant + managerial (not staff/kitchen).
  if (canSeeFinance) base.push({ to: "/imports", label: t("imports"), icon: Upload, testid: "nav-imports" });
  // Inventory: kitchen may see quantities; hide for waiter/staff.
  if (!["staff", "waiter"].includes(role) || isSuper) {
    base.push({ to: "/inventory", label: t("inventory"), icon: Boxes, testid: "nav-inventory" });
  }
  if (canManagePersonnel) base.push({ to: "/staff", label: t("staff"), icon: Users, testid: "nav-staff" });
  if (canSeeReports) base.push({ to: "/reports", label: t("reports"), icon: BarChart3, testid: "nav-reports" });
  // Settings only for managerial + super admin — never for staff/kitchen/accountant.
  if (canManagePersonnel) base.push({ to: "/settings", label: t("settings"), icon: Settings, testid: "nav-settings" });

  // Sprint 29 — HQ visible only to executive roles + super admin.
  const hqRoles = new Set(["organization_owner", "organization_admin", "owner", "restaurant_owner"]);
  if (isSuper || hqRoles.has(role)) {
    base.splice(1, 0, { to: "/hq", label: t("hq"), icon: Building2, testid: "nav-hq" });
  }
  return base;
};

const mobilePrimary = ["/", "/finance", "/inventory", "/reports"];

function BrandMark({ restaurant, size = 32 }) {
  if (restaurant?.logo_url) {
    return (
      <img
        src={restaurant.logo_url}
        alt=""
        className="rounded-lg object-cover shadow-elev-1"
        style={{ width: size, height: size }}
      />
    );
  }
  const letter = (restaurant?.brand_name || restaurant?.name || "R").slice(0, 1).toUpperCase();
  return (
    <div
      className="rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-display font-bold shadow-elev-1"
      style={{ width: size, height: size, fontSize: size * 0.5 }}
    >
      {letter}
    </div>
  );
}

export default function Layout() {
  const { t } = useI18n();
  const { user, restaurant, logout } = useAuth();
  const nav = useNavigate();
  const items = navItems(t, user);
  const brand = restaurant?.brand_name || restaurant?.name || "RestroPilot";
  const initial = (user?.full_name || user?.email || "U").slice(0, 1).toUpperCase();

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 md:border-r md:bg-muted/20 md:backdrop-blur-xl">
        <div className="h-16 px-5 flex items-center border-b">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark restaurant={restaurant} size={36} />
            <div className="min-w-0">
              <div className="font-display font-bold leading-none truncate tracking-tight">{brand}</div>
              <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground mt-1.5 font-medium">
                RestroPilot
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-elev-2"
                      : "text-foreground/65 hover:bg-background hover:text-foreground hover:shadow-elev-1"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="w-4 h-4 shrink-0" strokeWidth={isActive ? 2.25 : 2} />
                    <span className="truncate">{it.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t bg-background/40">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center font-semibold text-sm shadow-elev-1 shrink-0">
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate leading-tight">{user?.full_name}</div>
              <div className="text-[11px] text-muted-foreground truncate mt-0.5">{user?.email}</div>
            </div>
            <NotificationBell user={user} />
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" title={t("logout")} className="h-8 w-8 rounded-lg shrink-0">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 md:pl-64 flex flex-col min-h-screen">
        {/* Mobile top bar */}
        <header className="md:hidden h-14 border-b bg-background/85 backdrop-blur-xl sticky top-0 z-40 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <BrandMark restaurant={restaurant} size={30} />
            <div className="font-display font-bold truncate tracking-tight">{brand}</div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell user={user} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost" data-testid="mobile-menu-trigger" className="h-9 w-9 rounded-lg">
                  <MoreHorizontal className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <DropdownMenuLabel className="text-xs">{user?.full_name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {items.filter((i) => !mobilePrimary.includes(i.to)).map((i) => (
                  <DropdownMenuItem key={i.to} onSelect={() => nav(i.to)} data-testid={`m-${i.testid}`}>
                    <i.icon className="w-4 h-4 mr-2" /> {i.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleLogout} data-testid="m-logout">
                  <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 pb-20 md:pb-8">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t bg-background/90 backdrop-blur-xl">
          <div className="grid grid-cols-5">
            {items
              .filter((i) => mobilePrimary.includes(i.to))
              .concat([{ to: "/settings", label: t("more"), icon: MoreHorizontal, testid: "nav-more" }])
              .map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink
                    key={it.to + it.label}
                    to={it.to}
                    end={it.to === "/"}
                    data-testid={`bot-${it.testid}`}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors ${
                        isActive ? "text-primary" : "text-muted-foreground"
                      }`
                    }
                  >
                    <Icon className="w-5 h-5" />
                    <span>{it.label}</span>
                  </NavLink>
                );
              })}
          </div>
        </nav>
      </div>
    </div>
  );
}
